"use client";
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import HeroSection from './Hero/page';
import FAQPage from './FAQPage/page';
import VideSection from './VideSection/page';
import ServicesHome from './Service/page';
import Category from './category-home/page';
import { usePageSEO } from '@/components/SEO/withSEO';
import WorkFlow from './workFlow/page';
// import FreelnacerList from './dummy-freelancers/page';
import FeatureCard from './FeatureCard/page';
import Loader from '@/components/Loader/page';

export default function HomePage() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [fadeOut, setFadeOut] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const loadTimeoutRef = useRef(null);

  // SEO metadata automatically applied based on pathname
  const seoComponent = usePageSEO();

  // Memoized callback to hide loader
  const hideLoader = useCallback(() => {
    setFadeOut(true);
    loadTimeoutRef.current = setTimeout(() => {
      setIsLoading(false);
    }, 500);
  }, []);

  // Track when all images are loaded - optimized with passive listeners
  useEffect(() => {
    const images = Array.from(document.images);
    let loadedCount = 0;
    const imageListeners = new Map();
    let maxWaitTimer = null;

    const checkAllImagesLoaded = () => {
      loadedCount++;
      if (loadedCount === images.length) {
        setImagesLoaded(true);
        // Cleanup listeners and timer
        if (maxWaitTimer) clearTimeout(maxWaitTimer);
        imageListeners.forEach((listeners, img) => {
          img.removeEventListener('load', listeners.load);
          img.removeEventListener('error', listeners.error);
        });
        imageListeners.clear();
      }
    };

    if (images.length === 0) {
      setImagesLoaded(true);
    } else {
      images.forEach(img => {
        if (img.complete) {
          checkAllImagesLoaded();
        } else {
          const listeners = {
            load: checkAllImagesLoaded,
            error: checkAllImagesLoaded
          };
          imageListeners.set(img, listeners);
          // Use passive listeners for better performance
          img.addEventListener('load', listeners.load, { passive: true });
          img.addEventListener('error', listeners.error, { passive: true });
        }
      });

      // Set maximum wait time - don't let slow images block the page
      maxWaitTimer = setTimeout(() => {
        console.log('Max image load timeout reached - proceeding with page load');
        setImagesLoaded(true);
        // Cleanup listeners
        imageListeners.forEach((listeners, img) => {
          img.removeEventListener('load', listeners.load);
          img.removeEventListener('error', listeners.error);
        });
        imageListeners.clear();
      }, 5000); // 5 second maximum wait
    }

    return () => {
      if (maxWaitTimer) clearTimeout(maxWaitTimer);
      imageListeners.forEach((listeners, img) => {
        img.removeEventListener('load', listeners.load);
        img.removeEventListener('error', listeners.error);
      });
      imageListeners.clear();
    };
  }, []);

  // Hide loader when everything is loaded
  useEffect(() => {
    if (videoLoaded && imagesLoaded && document.readyState === 'complete') {
      hideLoader();
    }

    return () => {
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
      }
    };
  }, [videoLoaded, imagesLoaded, hideLoader]);

  // Listen to document ready state with passive listener
  useEffect(() => {
    const handleLoad = () => {
      if (videoLoaded && imagesLoaded) {
        hideLoader();
      }
    };

    if (document.readyState === 'complete') {
      handleLoad();
    } else {
      window.addEventListener('load', handleLoad, { passive: true });
      return () => window.removeEventListener('load', handleLoad);
    }
  }, [videoLoaded, imagesLoaded, hideLoader]);

  // Absolute max timeout - safety net to prevent infinite loader
  useEffect(() => {
    const absoluteMaxTimeout = setTimeout(() => {
      console.log('Absolute max timeout reached (7s) - forcing loader to hide');
      hideLoader();
    }, 3000); // 3 seconds absolute maximum

    return () => clearTimeout(absoluteMaxTimeout);
  }, [hideLoader]);

  // Handle BFCache (back/forward cache) restoration
  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) {
        // Page was restored from BFCache, reset loading state
        setIsLoading(false);
        setFadeOut(false);
      }
    };

    window.addEventListener('pageshow', handlePageShow, { passive: true });

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      // Clear all timeouts on unmount
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Check for Google signup parameters and redirect to complete-signup
    const googleId = searchParams.get('user_googleid');
    const googleEmail = searchParams.get('user_email');

    if (googleId && googleEmail) {
      // Redirect to complete-signup page with Google parameters
      const completeSignupUrl = `/complete-signup?user_googleid=${googleId}&user_email=${encodeURIComponent(googleEmail)}`;
      router.replace(completeSignupUrl);
      return;
    }

    // Check for Facebook signup parameters and redirect to complete-signup
    const facebookId = searchParams.get('user_facebookid') || searchParams.get('facebook_id');
    const facebookEmail = searchParams.get('user_email');

    if (facebookId) {
      const idParam = searchParams.get('user_facebookid') ? 'user_facebookid' : 'facebook_id';
      const completeSignupUrl = `/complete-signupfacebook?${idParam}=${encodeURIComponent(facebookId)}${facebookEmail ? `&user_email=${encodeURIComponent(facebookEmail)}` : ''}`;
      router.replace(completeSignupUrl);
      return;
    }

    // Only redirect to select-user-type for Google signup completion, not regular email registration
    // Regular email registration should stay on the homepage or go to login
    const message = searchParams.get('message');
    if (message === 'registration-complete') {
      // Clear the URL parameters to prevent repeated redirects
      router.replace('/');
      return;
    }
  }, [searchParams, router]);

  const handleVideoLoad = useCallback(() => {
    setVideoLoaded(true);
  }, []);

  return (
    <>
      {seoComponent}

      {/* Show loader until page is fully loaded */}
      {isLoading && <Loader fadeOut={fadeOut} />}

      <main className="overflow-x-hidden">
        <HeroSection onVideoLoad={handleVideoLoad} />
        <Category />
        <WorkFlow />
        <ServicesHome />
        <VideSection />
        {/* <FreelnacerList /> */}
        <FAQPage />
        <FeatureCard />
      </main>
    </>
  );
}